import {isFeatureEnabled} from 'feature-flags';
import {
  AvatarChoice,
  AvatarMultiChoice,
  Choice,
  Dialog,
  GetItemImage,
  MultiChoice,
  OptionStatusIndicator,
  showNotification,
} from 'platform/components';
import {HStack, Icon, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {always, isNil, sortBy, toLower} from 'ramda';
import {isArray, isNull, isString} from 'ramda-adjunct';

import {TeamResponseBody} from '@dms/api/core';
import {useLazyGetUserAvatarUrlQuery} from '@dms/api/customer';
import {GetUsersApiResponse} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useUserAbsenceIndicators} from '../../../hooks/useUserAbsenceIndicators';
import {handleApiError} from '../../../utils/handleApiError';
import {AssignmentSelection} from '../types/AssignmentSelection';
import {resolveMultiSelectValues} from '../utils/resolveMultiSelectValues';
import {isTeamValue, toTeamValue} from '../utils/teamValueHelpers';

type UserOptionType = {
  value: string;
  label: string;
  statusIndicator?: OptionStatusIndicator;
};

// Source: icon social/people.svg
// Used as a team avatar in AvatarChoice/AvatarMultiChoice to replace the user photo with a clean icon.
const TEAM_AVATAR_DATA_URI = `data:image/svg+xml,${encodeURIComponent('<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g transform="translate(3,3) scale(0.75)"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.99 8C10.99 9.66 9.66 11 8 11C6.34 11 5 9.66 5 8C5 6.34 6.34 5 8 5C9.66 5 10.99 6.34 10.99 8ZM18.99 8C18.99 9.66 17.66 11 16 11C14.34 11 13 9.66 13 8C13 6.34 14.34 5 16 5C17.66 5 18.99 6.34 18.99 8ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM15.03 13.05C15.38 13.02 15.71 13 16 13C18.33 13 23 14.17 23 16.5V19H17V16.5C17 15.02 16.19 13.89 15.03 13.05Z" fill="#172B4D"/></g></svg>')}`;

const buildUserOptions = (
  users: GetUsersApiResponse | undefined,
  getStatusIndicator: (userId: string) => OptionStatusIndicator | undefined
) =>
  users?.reduce<UserOptionType[]>(
    (result, user) =>
      user.blocked
        ? result
        : result.concat({
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
            statusIndicator: getStatusIndicator(user.id),
          }),
    []
  ) ?? [];

const buildTeamOptions = (teams: TeamResponseBody[] | undefined) =>
  teams?.map((team) => ({value: toTeamValue(team.id), label: team.name})) ?? [];

const buildGroupedOptions = (
  userOptions: UserOptionType[],
  teamOptions: UserOptionType[]
): OptionGroup[] => {
  const sortedTeams = sortBy((option) => toLower(option.label), teamOptions);
  const sortedUsers = sortBy((option) => toLower(option.label), userOptions);

  const groups: OptionGroup[] = [];

  if (sortedTeams.length > 0) {
    groups.push({
      label: i18n.t('general.labels.participation.teamGroup'),
      options: sortedTeams,
    });
  }

  if (sortedUsers.length > 0) {
    groups.push({
      label: i18n.t('general.labels.participation.userGroup'),
      options: sortedUsers,
    });
  }

  return groups;
};

const formatTeamOptionLabel = (option: {value?: string | null; label?: string | null}) => {
  if (option.value && isTeamValue(option.value)) {
    return (
      <HStack spacing={2} align="center">
        <Icon value="social/people" size={4} color="text.secondary" />
        <Text size="small">{option.label}</Text>
      </HStack>
    );
  }
  return option.label;
};

const hasTeamSelected = (value: string | string[] | null) => {
  if (isString(value)) {
    return isTeamValue(value);
  }
  if (isArray(value)) {
    return value.some(isTeamValue);
  }
  return false;
};

type OptionGroup = {
  label: string;
  options: UserOptionType[];
};

interface EditUserDialogProps extends TestIdProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  updateAssignmentSelection: (newState: AssignmentSelection | null) => void;
  deleteErrorMessage: () => void;
  assignmentSelection: AssignmentSelection;
  users: GetUsersApiResponse | undefined;
  teams?: TeamResponseBody[];
  isLoading?: boolean;
  isErrorMessage?: boolean;
}

export function EditUserDialog(props: EditUserDialogProps) {
  const [getUserAvatar] = useLazyGetUserAvatarUrlQuery();
  const {getStatusIndicator} = useUserAbsenceIndicators();

  const userOptions = buildUserOptions(props.users, getStatusIndicator);
  const teamOptions = buildTeamOptions(props.teams);
  const hasTeams = teamOptions.length > 0;

  const options = hasTeams ? buildGroupedOptions(userOptions, teamOptions) : userOptions;

  const handleSelect = (value: string | number | string[] | null) => {
    if (isArray(value) || isString(value) || isNull(value)) {
      props.deleteErrorMessage();
      props.updateAssignmentSelection({...props.assignmentSelection, value});
    } else {
      showNotification.error();
    }
  };

  const handleMultiSelect = (newValues: string[] | null) => {
    const previousValues = isArray(props.assignmentSelection.value)
      ? props.assignmentSelection.value
      : [];

    const values = resolveMultiSelectValues(previousValues, newValues);
    handleSelect(values);
  };

  const isAssigneesDialog = props.assignmentSelection?.type === 'assignees';
  const dialogTitle = isAssigneesDialog
    ? i18n.t('general.labels.addPeople')
    : i18n.t('general.labels.owner');

  const showTeamHelperText = hasTeamSelected(props.assignmentSelection.value);
  const teamHelperText = showTeamHelperText
    ? i18n.t('general.labels.participation.teamHelperText')
    : undefined;

  const getItemImage: GetItemImage<string> = useCallback(
    async (item) => {
      if (!item.value) {
        return null;
      }
      if (isTeamValue(item.value)) {
        return TEAM_AVATAR_DATA_URI;
      }
      const imageUrl = await getUserAvatar({userId: item.value, height: 120, width: 120}, true)
        .unwrap()
        .catch(handleApiError);

      return imageUrl ?? null;
    },
    [getUserAvatar]
  );

  const dialogElement = match([isAssigneesDialog, isFeatureEnabled(featureFlags.EMPLOYEE_AVATARS)])
    .with([true, false], () => (
      <MultiChoice
        value={match([
          isArray(props.assignmentSelection.value),
          isNil(props.assignmentSelection.value),
        ])
          .with([true, false], always(props.assignmentSelection.value as string[]))
          .with([false, false], always([props.assignmentSelection.value as string]))
          .otherwise(always([]))}
        options={options}
        onChange={hasTeams ? handleMultiSelect : handleSelect}
        closeMenuOnSelect={!isAssigneesDialog}
        hasOptionCheckbox={isAssigneesDialog}
        placeholder={i18n.t(`general.labels.participation.type.${props.assignmentSelection?.type}`)}
        formatOptionLabel={hasTeams ? formatTeamOptionLabel : undefined}
        helperText={teamHelperText}
        isLoading={props.isLoading}
        data-testid={suffixTestId('assignee', props)}
      />
    ))
    .with([true, true], () => (
      <AvatarMultiChoice
        value={match([
          isArray(props.assignmentSelection.value),
          isNil(props.assignmentSelection.value),
        ])
          .with([true, false], always(props.assignmentSelection.value as string[]))
          .with([false, false], always([props.assignmentSelection.value as string]))
          .otherwise(always([]))}
        options={options}
        onChange={hasTeams ? handleMultiSelect : handleSelect}
        closeMenuOnSelect={!isAssigneesDialog}
        hasOptionCheckbox={isAssigneesDialog}
        placeholder={i18n.t(`general.labels.participation.type.${props.assignmentSelection?.type}`)}
        helperText={teamHelperText}
        isLoading={props.isLoading}
        data-testid={suffixTestId('assignee', props)}
        getItemImage={getItemImage}
      />
    ))
    .with([false, false], () => (
      <Choice
        errorMessage={props.isErrorMessage ? i18n.t('general.labels.required') : undefined}
        value={
          isArray(props.assignmentSelection.value)
            ? props.assignmentSelection.value[0]
            : props.assignmentSelection.value
        }
        options={options}
        onChange={handleSelect}
        closeMenuOnSelect={!isAssigneesDialog}
        hasOptionCheckbox={isAssigneesDialog}
        placeholder={i18n.t(`general.labels.participation.type.${props.assignmentSelection?.type}`)}
        formatOptionLabel={hasTeams ? formatTeamOptionLabel : undefined}
        helperText={teamHelperText}
        isLoading={props.isLoading}
        data-testid={suffixTestId('assignee', props)}
      />
    ))
    .with([false, true], () => (
      <AvatarChoice
        errorMessage={props.isErrorMessage ? i18n.t('general.labels.required') : undefined}
        value={
          isArray(props.assignmentSelection.value)
            ? props.assignmentSelection.value[0]
            : props.assignmentSelection.value
        }
        options={options}
        onChange={handleSelect}
        getItemImage={getItemImage}
        closeMenuOnSelect={!isAssigneesDialog}
        hasOptionCheckbox={isAssigneesDialog}
        placeholder={i18n.t(`general.labels.participation.type.${props.assignmentSelection?.type}`)}
        helperText={teamHelperText}
        isLoading={props.isLoading}
        data-testid={suffixTestId('assignee', props)}
      />
    ))
    .exhaustive();

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      size="small"
      scrollBehavior="outside"
      title={dialogTitle}
      buttons={[
        {variant: 'secondary', onClick: props.onClose, title: i18n.t('general.actions.discard')},
        {variant: 'primary', onClick: props.onSave, title: i18n.t('general.actions.save')},
      ]}
      data-testid={suffixTestId('dialog', props)}
    >
      {dialogElement}
    </Dialog>
  );
}
